/**
 * Latest-message timestamp resolution for the conversation list.
 *
 * Both the row timestamp and the list ordering must come from the newest
 * message in the thread. Conversation and group metadata (created / updated /
 * member activity) is deliberately never consulted: a group created last month
 * whose last message arrived this morning has to sort and read as this
 * morning.
 *
 * Endpoints differ in where they put that value — a flat field on the list
 * payload, or nested on the last-message object of the detail payload — so
 * every known latest-message field is checked, newest wins.
 */
import { parseToDate } from '../../utils/dateHelpers';
import type { ConversationDto } from './conversationTypes';

/**
 * Reads a backend timestamp as UTC. Conversation endpoints answer in UTC and
 * often omit the designator; `parseToDate(..., 'utc')` covers ISO, .NET
 * short-date, and space-separated forms without appending a broken `Z`.
 */
export const parseUtcTimestamp = (value?: string | null): Date | null => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return null;
  return parseToDate(raw, 'utc');
};

const toEpoch = (value?: string | null): number => {
  const parsed = parseUtcTimestamp(value);
  return parsed ? parsed.getTime() : 0;
};

/** Latest-message timestamp fields seen across the conversation endpoints. */
const LAST_MESSAGE_FIELDS = [
  'lastMessageAt',
  'lastMessageOn',
  'lastMessageDate',
  'lastMessageTime',
  'lastMessageSentAt',
  'lastMessageCreatedAt',
  'lastMessageDateTime',
] as const;

/** Timestamp fields on a message object. */
const MESSAGE_FIELDS = ['sentAt', 'createdAt', 'messageDate', 'timestamp'] as const;

/** Any payload shape carrying one of the message timestamp fields. */
export type TimestampedMessage = Partial<
  Record<(typeof MESSAGE_FIELDS)[number], string | null | undefined>
>;

/** Newest of the timestamp fields present on a message object. */
const messageEpoch = (message: TimestampedMessage): number => {
  const fields = message as Record<string, unknown>;
  let latest = 0;
  for (const field of MESSAGE_FIELDS) {
    const value = fields[field];
    if (typeof value !== 'string') continue;
    const epoch = toEpoch(value);
    if (epoch > latest) latest = epoch;
  }
  return latest;
};

/**
 * Epoch milliseconds of the newest message, or 0 when the thread has none.
 * Group and direct conversations run through this same resolver.
 */
export const resolveLastMessageAt = (dto: ConversationDto): number => {
  const candidates: number[] = [];

  const flat = dto as unknown as Record<string, unknown>;
  for (const field of LAST_MESSAGE_FIELDS) {
    const value = flat[field];
    if (typeof value === 'string') candidates.push(toEpoch(value));
    // A few list endpoints answer with epoch milliseconds instead of a string.
    else if (typeof value === 'number' && Number.isFinite(value)) candidates.push(value);
  }

  const nested = dto.lastMessage;
  if (nested && typeof nested === 'object') {
    candidates.push(messageEpoch(nested));
  }

  return candidates.reduce((latest, value) => (value > latest ? value : latest), 0);
};

/**
 * Newest `createdAt` in a loaded message page. Used when the list payload
 * carried no latest-message timestamp but the thread's messages are known.
 */
export const resolveLatestMessageTimestamp = (
  messages: readonly TimestampedMessage[] | null | undefined,
): number => {
  if (!messages?.length) return 0;
  return messages.reduce((latest, message) => {
    const value = messageEpoch(message);
    return value > latest ? value : latest;
  }, 0);
};
